import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Paper, Stepper, Step, StepLabel, StepContent, Button, Chip } from '@mui/material';
import gsap from 'gsap';

/**
 * @component BlockchainTraceability
 * @description Blockchain traceability visualization component for ethical mineral supply chain
 *
 * SEO Considerations:
 * - Demonstrates blockchain verification process for ethical mineral sourcing
 * - Visualizes complete supply chain from mine registration to manufacturer receipt
 * - Includes keyword-rich content about mineral traceability and verification
 * - Uses semantic HTML structure with proper heading hierarchy
 * - Provides text alternatives for visual blockchain representations
 * - Highlights ethical sourcing verification through blockchain technology
 */
const BlockchainTraceability = () => {
  const [activeStep, setActiveStep] = useState(0);
  const containerRef = useRef(null);
  const blockchainRef = useRef(null);
  const blocksRef = useRef([]);

  // Sample blockchain data
  const blockchainData = [
    {
      step: 'Mine Registration',
      description: 'Mine location, ownership, and certification details are recorded on the blockchain',
      date: '2023-10-15',
      hash: '0x7f83b1657ff1fc53b92dc18148a1d65dfc2d4b1fa3d677284addd200126d9069',
      location: 'Kolwezi, DRC',
      entity: 'Cobalt Mining Ltd.',
      details: 'Registered with certification #DRC-2023-CM-1289'
    },
    {
      step: 'Mineral Extraction',
      description: 'Details of extracted minerals including quantity, quality, and date are recorded',
      date: '2023-11-02',
      hash: '0x3c9909afec25354d551dae21590bb26e38d53f2173b8d3dc3eee4c047e7ab1c1',
      location: 'Kolwezi, DRC',
      entity: 'Cobalt Mining Ltd.',
      details: 'Extracted 500kg of Cobalt ore, Grade A'
    },
    {
      step: 'Processing & Refining',
      description: 'Processing details including methods, outputs, and quality tests are recorded',
      date: '2023-11-18',
      hash: '0x2c624232cdd221771294dfbb310aca000a0df6ac8b66b696d90ef06fdefb64a3',
      location: 'Lubumbashi, DRC',
      entity: 'African Refineries Inc.',
      details: 'Refined to 98.5% purity, 450kg output'
    },
    {
      step: 'Quality Certification',
      description: 'Independent quality certification and compliance verification',
      date: '2023-11-25',
      hash: '0x19581e27de7ced00ff1ce50b2047e7a567c76b1cbaebabe5ef03f7c3017bb5b7',
      location: 'Johannesburg, South Africa',
      entity: 'Global Mineral Standards',
      details: 'Certified compliant with ISO 9001:2015'
    },
    {
      step: 'Transportation',
      description: 'Shipping details including carrier, route, and handling procedures',
      date: '2023-12-05',
      hash: '0x4a44dc15364204a80fe80e9039455cc1608281820fe2b24f1e5233ade6af1dd5',
      location: 'In Transit',
      entity: 'Global Logistics Partners',
      details: 'Shipped via secure container #GL-78291'
    },
    {
      step: 'Manufacturer Receipt',
      description: 'Confirmation of receipt by the manufacturer with quality verification',
      date: '2023-12-20',
      hash: '0x4fc82b26aecb47d2868c4efbe3581732a3e7cbcc6c2efb32062c08170a05eeb8',
      location: 'Shanghai, China',
      entity: 'TechBattery Manufacturing',
      details: 'Received 450kg, quality verified'
    }
  ];

  /**
   * Auto-advance blockchain verification steps for ethical mineral supply chain
   * - Cycles through each step of the mineral supply chain verification process
   * - Demonstrates the complete traceability from mine to manufacturer
   * - Enhances user experience while maintaining SEO-friendly content
   */
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prevStep) => {
        // If we're at the last step, go back to the first step
        if (prevStep >= blockchainData.length - 1) {
          return 0;
        }
        // Otherwise, advance to the next step
        return prevStep + 1;
      });
    }, 5000); // Change step every 5 seconds

    return () => clearInterval(interval);
  }, [blockchainData.length]);

  // Handle step navigation (for manual control if needed)
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  /**
   * Animate blockchain blocks to visualize ethical mineral supply chain verification
   * - Highlights the current step in the mineral traceability process
   * - Visually represents the immutable blockchain record of mineral sourcing
   * - Enhances user understanding of blockchain verification for ethical minerals
   */
  useEffect(() => {
    if (blocksRef.current.length > 0) {
      // Reset all blocks
      gsap.to(blocksRef.current, {
        backgroundColor: '#e0e0e0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        scale: 1,
        duration: 0.3
      });

      // Highlight active block
      gsap.to(blocksRef.current[activeStep], {
        backgroundColor: '#eab308',
        boxShadow: '0 4px 12px rgba(234, 179, 8, 0.4)',
        scale: 1.05,
        duration: 0.5,
        ease: 'power2.out'
      });

      // Animate connection lines
      gsap.to('.blockchain-connection', {
        strokeDashoffset: 0,
        duration: 1,
        ease: 'power1.inOut'
      });
    }
  }, [activeStep]);

  return (
    <Paper
      ref={containerRef}
      aria-label="Blockchain verification process for ethical mineral sourcing"
      sx={{
        position: 'relative',
        width: '100%',
        borderRadius: 3,
        border: '2px solid #f5e5a5',
        borderLeft: '6px solid #eab308',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        background: 'linear-gradient(to right, #fffef0, #fffde7)',
        p: 3,
        mb: 0,
        mt: 0,
        overflow: 'hidden'
      }}
    >
      <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
        Blockchain-Powered Ethical Mineral Traceability
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
        {/* Blockchain visualization */}
        <Box
          ref={blockchainRef}
          sx={{
            flex: { xs: '1', md: '0 0 40%' },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
            py: 2
          }}
        >
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
            Immutable Blockchain Record
          </Typography>

          {/* Blockchain blocks */}
          <Box sx={{ position: 'relative', width: '100%', height: 400 }}>
            {/* Connection lines */}
            <svg
              width="100%"
              height="100%"
              style={{ position: 'absolute', top: 0, left: 0, zIndex: 0 }}
            >
              {blockchainData.map((_, index) => {
                if (index < blockchainData.length - 1) {
                  return (
                    <line
                      key={`line-${index}`}
                      x1="50%"
                      y1={`${(index + 0.5) * (100 / blockchainData.length)}%`}
                      x2="50%"
                      y2={`${(index + 1) * (100 / blockchainData.length)}%`}
                      stroke="#eab308"
                      strokeWidth="2"
                      strokeDasharray="5,5"
                      className="blockchain-connection"
                      style={{ strokeDashoffset: 100 }}
                    />
                  );
                }
                return null;
              })}
            </svg>

            {blockchainData.map((block, index) => (
              <Box
                key={`block-${index}`}
                ref={el => blocksRef.current[index] = el}
                sx={{
                  position: 'absolute',
                  top: `${index * (100 / blockchainData.length)}%`,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '80%',
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: index === activeStep ? '#eab308' : '#e0e0e0',
                  color: index === activeStep ? 'white' : 'text.primary',
                  boxShadow: index === activeStep
                    ? '0 4px 12px rgba(234, 179, 8, 0.4)'
                    : '0 2px 8px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  zIndex: 1,
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                    transform: 'translateX(-50%) scale(1.02)'
                  }
                }}
                onClick={() => setActiveStep(index)}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {block.step}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                  Hash: {block.hash.substring(0, 10)}...
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Step details */}
        <Box sx={{ flex: { xs: '1', md: '0 0 60%' } }}>
          <Stepper activeStep={activeStep} orientation="vertical">
            {blockchainData.map((step, index) => (
              <Step key={step.step}>
                <StepLabel>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {step.step}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" paragraph>
                    {step.description}
                  </Typography>

                  <Paper sx={{ p: 2, bgcolor: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                      <Chip label={`Date: ${step.date}`} size="small" />
                      <Chip label={`Location: ${step.location}`} size="small" />
                      <Chip label={`Entity: ${step.entity}`} size="small" />
                    </Box>
                    <Typography variant="body2">
                      {step.details}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, fontFamily: 'monospace' }}>
                      Block Hash: {step.hash}
                    </Typography>
                  </Paper>

                  {/* Animation runs automatically */}
                </StepContent>
              </Step>
            ))}
          </Stepper>

          {activeStep === blockchainData.length && (
            <Paper square elevation={0} sx={{ p: 3, bgcolor: 'rgba(255, 255, 255, 0.7)' }}>
              <Typography variant="subtitle1" gutterBottom>
                Complete traceability from mine to manufacturer
              </Typography>
              <Typography variant="body2" paragraph>
                Every step in the supply chain is permanently recorded on the blockchain,
                ensuring complete transparency and accountability.
              </Typography>
              {/* Animation runs automatically in a loop */}
            </Paper>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default BlockchainTraceability;
